import {
  clearSession,
  IPublicSession,
  isSessionOrganizer,
  resetSession,
  sharedSessionSchema,
  updatePublicSession,
  updatePublicSessionNow,
  userSession,
} from 'state/session';
import { user } from 'utils/auth/user';
import { State } from 'utils/state';
import { IntegrationType, integrationType } from 'utils/ui/app';

export function syncToServer(state: State<IPublicSession>) {
  state.on('change', (keys) => {
    const updateEverything =
      integrationType() === IntegrationType.WebComponent && userSession().get('activeExperience') === '2D';
    // Block syncing when offline (except for offline flag changes)
    if (userSession().get('offline') && !keys.includes('offline')) {
      console.warn('Session sync blocked: offline mode is enabled');
      return;
    }

    // do not proceed if session is starting or ending
    if (keys.includes('id') || keys.includes('endedAt')) {
      if (keys.includes('endedAt') && integrationType() === IntegrationType.WebComponent) {
        // Reset ended session for web component case (handled outside of wc context in standalone)
        clearSession();
        resetSession();
      }
      // AVS-4080: When endedAt arrives in state (e.g. loadPublicSession resolved with a
      // soft-deleted scheduled session), cancel any pending debounced PUT. Otherwise a
      // pre-load brand/market write queued by contextLoader will fire ~1s later with the
      // ended payload, the lambda rejects with 404 SESSION_NOT_FOUND, and sendApiRequest
      // renders a duplicate red toast on top of the page's own error UI.
      if (keys.includes('endedAt')) {
        updatePublicSession.cancel();
      }
      return;
    }

    // AVS-4080: Skip syncing once the session is ended. Loading a soft-deleted
    // scheduled session populates state with endedAt; downstream listeners (e.g.
    // initCarSelectionMethod) then mutate other fields and would otherwise trigger
    // a PUT that includes endedAt in the body. Cancel any pending debounce here too
    // in case it slipped past the change-with-endedAt branch above.
    if (userSession().get('endedAt')) {
      updatePublicSession.cancel();
      return;
    }

    // Customer should never sync aveSessionId to server (dealer-only field)
    // This prevents race conditions where customer's initial load overwrites dealer's value
    if (!user.isDealer && (keys.includes('aveSessionId') || keys.includes('aveChannelName'))) {
      console.warn('Customer cannot sync aveSessionId or aveChannelName to server. Skipping update.');
      return;
    }

    // Only the session organizer can change activeExperience (AVS-4141)
    // Non-organizer dealers must follow the organizer's experience choice
    if (user.isDealer && keys.includes('activeExperience') && !isSessionOrganizer()) {
      console.warn('Non-organizer cannot sync activeExperience to server. Skipping update.');
      return;
    }

    if (userSession().get('isShared') && (keys.includes('brand') || keys.includes('market'))) {
      console.warn('Changes to brand or market while sharing are not allowed. Skipping session update.');
      return;
    }

    // only keys that are part of the internal session schema should trigger an update on database level
    // if none of those keys changed, we can skip the database update and just emit the event to the participants
    const relevantKeys = Object.keys(sharedSessionSchema.shape);
    if (!relevantKeys.some((key) => keys.includes(key))) {
      console.warn('No relevant keys changed for session update: ', keys);
      updatePublicSession({ emitOnly: true, updateEverything });
      return;
    }

    // aveSessionId is critical for multi-dealer sync — bypass the debounce and send
    // immediately. This ensures a joining dealer reads the correct aveSessionId from
    // the server before creating its own AVE session. (AVS-4141)
    // Note: activeExperience intentionally uses the debounce to avoid a last-writer-wins
    // race between two dealers' immediate PUTs.
    if (keys.includes('aveSessionId')) {
      updatePublicSession.cancel();
      updatePublicSessionNow({ updateEverything });
      return;
    }

    // send session data to the server (debounced)
    updatePublicSession({ updateEverything });
  });
}
